// Lightweight per-route SEO: updates title, meta description, canonical and og:* tags.
// Returns a cleanup function that restores the previous values.

const SITE = "https://amplificador.instructivacursos.com.br";

type SeoOptions = {
  title: string;
  description: string;
  path: string; // e.g. "/Obrigado-Trafego"
  ogType?: "website" | "article";
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
};

function setMeta(selector: string, attr: "name" | "property", key: string, value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function applySeo({ title, description, path, ogType = "website", jsonLd }: SeoOptions) {
  const url = `${SITE}${path}`;
  const JSONLD_ID = "route-jsonld";
  const prev = {
    title: document.title,
    description:
      document.head.querySelector<HTMLMetaElement>('meta[name="description"]')?.content ?? "",
    canonical:
      document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.href ?? "",
    ogTitle:
      document.head.querySelector<HTMLMetaElement>('meta[property="og:title"]')?.content ?? "",
    ogDesc:
      document.head.querySelector<HTMLMetaElement>('meta[property="og:description"]')?.content ?? "",
    ogUrl:
      document.head.querySelector<HTMLMetaElement>('meta[property="og:url"]')?.content ?? "",
    ogType:
      document.head.querySelector<HTMLMetaElement>('meta[property="og:type"]')?.content ?? "",
    twTitle:
      document.head.querySelector<HTMLMetaElement>('meta[name="twitter:title"]')?.content ?? "",
    twDesc:
      document.head.querySelector<HTMLMetaElement>('meta[name="twitter:description"]')?.content ?? "",
  };

  document.title = title;
  setMeta('meta[name="description"]', "name", "description", description);
  setLink("canonical", url);
  setMeta('meta[property="og:title"]', "property", "og:title", title);
  setMeta('meta[property="og:description"]', "property", "og:description", description);
  setMeta('meta[property="og:url"]', "property", "og:url", url);
  setMeta('meta[property="og:type"]', "property", "og:type", ogType);
  setMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
  setMeta('meta[name="twitter:description"]', "name", "twitter:description", description);

  // Route-scoped JSON-LD
  document.getElementById(JSONLD_ID)?.remove();
  if (jsonLd) {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = JSONLD_ID;
    s.text = JSON.stringify(jsonLd);
    document.head.appendChild(s);
  }

  return () => {
    document.title = prev.title;
    setMeta('meta[name="description"]', "name", "description", prev.description);
    setLink("canonical", prev.canonical);
    setMeta('meta[property="og:title"]', "property", "og:title", prev.ogTitle);
    setMeta('meta[property="og:description"]', "property", "og:description", prev.ogDesc);
    setMeta('meta[property="og:url"]', "property", "og:url", prev.ogUrl);
    setMeta('meta[property="og:type"]', "property", "og:type", prev.ogType || "website");
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", prev.twTitle);
    setMeta('meta[name="twitter:description"]', "name", "twitter:description", prev.twDesc);
    document.getElementById(JSONLD_ID)?.remove();
  };
}
